import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useToast } from '../contexts/ToastContext';
import { getApiErrorMessage } from '../utils/apiError';
import { Eye, EyeOff, CheckCircle2 } from 'lucide-react';
import useReducedMotion from '../hooks/useReducedMotion';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [showSuccess, setShowSuccess] = useState(false);
  const reduced = useReducedMotion();
  const { loginWithCredentials, loginWithGoogle } = useAuth();
  const { t, lang } = useLanguage();
  const toast = useToast();
  const navigate = useNavigate();

  // Allow safe internal returnTo from 401 interceptor.
  // Rol bazlı varsayılan hedef: danışman rolü öğrenci dashboard'una değil,
  // kendi çalışma ekranına düşmeli. Danışmana özel ana sayfa (`/danisman`)
  // devrede — geri kalan mantık (explicit returnTo önceliği, güvenlik
  // kontrolleri) aynı kalır.
  const getReturnTo = (role) => {
    const roleDefault = role === 'danisman' ? '/danisman' : '/dashboard';
    const raw = new URLSearchParams(window.location.search).get('returnTo');
    if (!raw) return roleDefault;
    let decoded;
    try { decoded = decodeURIComponent(raw); } catch { return roleDefault; }
    if (!decoded.startsWith('/') || decoded.startsWith('//') ||
        decoded.startsWith('/\\') || decoded.startsWith('/login')) {
      return roleDefault;
    }
    return decoded;
  };

  useEffect(() => {
    document.body.classList.add('duo-page');
    return () => document.body.classList.remove('duo-page');
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const userData = await loginWithCredentials(email, password);
      // If backend signals first-login, redirect to password change page
      if (userData && userData.must_change_password) {
        navigate('/change-password', { replace: true });
        return;
      }
      toast.success(t('auth.duo.login.toastSuccess'));
      setShowSuccess(true);
      const target = getReturnTo(userData?.role);
      window.setTimeout(() => navigate(target), reduced ? 0 : 550);
    } catch (err) {
      const msg = getApiErrorMessage(err, lang);
      setError(msg);
      toast.error(msg);
      setShakeKey((k) => k + 1);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleClick = () => {
    const result = loginWithGoogle?.();
    if (result && typeof result.then === 'function') return;
    toast.info(t('auth.duo.login.googleDisabled'), { duration: 6000 });
  };

  return (
    <div className="min-h-screen flex items-center justify-center duo-bg px-4 py-10" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <motion.div
          className="text-center mb-8"
          initial={reduced ? false : { opacity: 0, y: -10 }}
          animate={reduced ? undefined : { opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
        >
          <Link to="/" className="inline-flex items-center gap-3">
            <div className="w-11 h-11 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--duo-green)' }}>
              <span className="text-white font-duo-display text-2xl leading-none">K</span>
            </div>
            <span className="font-duo-display text-2xl text-[var(--duo-text)]">Kariyer Online</span>
          </Link>
        </motion.div>

        <motion.div
          key={shakeKey}
          className="relative bg-white rounded-2xl p-8"
          initial={shakeKey === 0 ? (reduced ? false : { opacity: 0, scale: 0.96 }) : false}
          animate={shakeKey === 0
            ? (reduced ? undefined : { opacity: 1, scale: 1 })
            : (reduced ? undefined : { x: [0, -8, 8, -6, 6, -3, 3, 0] })}
          transition={shakeKey === 0
            ? { duration: 0.4, ease: [0.22, 0.61, 0.36, 1] }
            : { duration: 0.45, ease: [0.65, 0, 0.35, 1] }}
          style={{ border: '2px solid var(--duo-stroke)' }}
        >
          <h2 className="font-duo-display text-3xl text-center text-[var(--duo-text)] mb-2" data-testid="auth-title">
            {t('auth.duo.welcomeBack')}
          </h2>
          <p className="text-center text-sm text-[var(--duo-text-soft)] mb-7 font-medium">
            {t('auth.duo.login.title')}
          </p>

          <button
            type="button"
            onClick={handleGoogleClick}
            className="btn-duo-secondary w-full mb-4"
            data-testid="google-login-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" aria-hidden="true">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            {t('auth.duo.continueGoogle')}
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-[var(--duo-stroke)]"></div>
            <span className="text-xs text-[var(--duo-text-light)] font-bold uppercase tracking-widest">{t('auth.duo.or')}</span>
            <div className="flex-1 h-px bg-[var(--duo-stroke)]"></div>
          </div>

          {error && (
            <div
              className="bg-[#FFF5F5] border-2 border-[var(--duo-error)] text-[var(--duo-error-dark)] px-4 py-2 rounded-xl mb-4 text-sm font-bold"
              data-testid="auth-error"
            >
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[var(--duo-text-soft)] mb-2">
                {t('auth.duo.email')}
              </label>
              <input
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder={t('auth.duo.emailPlaceholder')}
                className="input-duo"
                required
                data-testid="login-email-input"
              />
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-wider text-[var(--duo-text-soft)] mb-2">
                {t('auth.duo.password')}
              </label>
              <div className="input-duo-wrap">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  placeholder={t('auth.duo.passwordPlaceholder')}
                  className="input-duo"
                  required
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="input-duo-affix"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              <div className="text-right mt-2">
                <Link
                  to="/forgot-password"
                  className="text-xs font-bold uppercase tracking-wider text-[var(--duo-blue)] hover:underline"
                  data-testid="forgot-password-link"
                >
                  {t('auth.duo.forgotLink')}
                </Link>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="btn-duo-primary w-full mt-4"
              data-testid="login-submit-btn"
            >
              {loading ? t('auth.duo.submitting') : t('auth.duo.login.title')}
            </button>
          </form>

          <p className="text-center mt-7 text-sm text-[var(--duo-text-soft)] font-medium" data-testid="no-account-info">
            {t('auth.duo.noAccountInfo')}
          </p>

          <AnimatePresence>
            {showSuccess && (
              <motion.div
                className="absolute inset-0 flex items-center justify-center bg-white/85 backdrop-blur-sm rounded-2xl"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                data-testid="auth-success-overlay"
              >
                <motion.div
                  initial={reduced ? false : { scale: 0.5, opacity: 0 }}
                  animate={reduced ? undefined : { scale: [0.5, 1.2, 0.95, 1], opacity: 1 }}
                  transition={{ duration: 0.55, ease: [0.22, 0.61, 0.36, 1], times: [0, 0.5, 0.8, 1] }}
                  className="flex flex-col items-center gap-3"
                >
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center"
                    style={{ backgroundColor: 'var(--duo-green)', boxShadow: '0 4px 0 0 var(--duo-green-dark)' }}
                  >
                    <CheckCircle2 size={40} strokeWidth={2.5} className="text-white" />
                  </div>
                  <p className="font-duo-display text-xl text-[var(--duo-text)]">
                    {t('auth.duo.success')}
                  </p>
                </motion.div>
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
